// Google Places loader + address-component parsing. Reuses the same key/library
// as the classic (CheckoutWC) checkout: maps/api/js?libraries=places&language=el.

let loadingPromise = null;

export function loadPlaces() {
  const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};
  const key = cfg.googlePlacesKey;
  if (!key) return Promise.reject(new Error('no-google-key'));
  if (window.google && window.google.maps && window.google.maps.places) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&libraries=places&language=el';
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('google-load-failed'));
    s.onload = () => {
      // With libraries=places the namespace is ready on load, but poll briefly
      // to be safe against timing.
      let n = 30;
      (function check() {
        if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete) resolve();
        else if (n-- > 0) setTimeout(check, 100);
        else reject(new Error('places-not-ready'));
      })();
    };
    document.head.appendChild(s);
  });
  return loadingPromise;
}

// Greece doesn't expose the periphery (administrative_area_level_1) in Google's
// address data — only the regional unit (level_3). So we derive the WC region
// code from the postcode's first two digits (reliable, and the field is
// optional/editable anyway). Codes match WC()->countries->get_states('GR').
const TK2_TO_REGION = {
  10: 'I', 11: 'I', 12: 'I', 13: 'I', 14: 'I', 15: 'I', 16: 'I', 17: 'I', 18: 'I', 19: 'I', // Αττική
  20: 'J', 21: 'J', 22: 'J', 23: 'J', 24: 'J',                                             // Πελοπόννησος
  25: 'G', 26: 'G', 27: 'G',                                                               // Δυτική Ελλάδα
  28: 'F', 29: 'F',                                                                        // Ιόνια νησιά
  30: 'G',                                                                                 // Αιτωλοακαρνανία
  31: 'F',                                                                                 // Λευκάδα
  32: 'H', 33: 'H', 34: 'H', 35: 'H', 36: 'H',                                             // Κεντρική Ελλάδα (Στερεά)
  37: 'E', 38: 'E', 39: 'E', 40: 'E', 41: 'E', 42: 'E', 43: 'E', 44: 'E',                 // Θεσσαλία
  45: 'D', 46: 'D', 47: 'D', 48: 'D',                                                      // Ήπειρος
  49: 'F',                                                                                 // Κέρκυρα
  50: 'C', 51: 'C', 52: 'C', 53: 'C',                                                      // Δυτική Μακεδονία
  54: 'B', 55: 'B', 56: 'B', 57: 'B', 58: 'B', 59: 'B', 60: 'B', 61: 'B', 62: 'B', 63: 'B', // Κεντρική Μακεδονία
  64: 'A', 65: 'A', 66: 'A', 67: 'A', 68: 'A', 69: 'A',                                    // Αν. Μακεδονία & Θράκη
  70: 'M', 71: 'M', 72: 'M', 73: 'M', 74: 'M',                                             // Κρήτη
  80: 'F',                                                                                 // Κεφαλονιά
  81: 'K', 82: 'K', 83: 'K',                                                               // Βόρειο Αιγαίο
  84: 'L', 85: 'L',                                                                        // Νότιο Αιγαίο
};

export function regionFromPostcode(postcode) {
  const d = (String(postcode || '').match(/\d/g) || []).join('');
  if (d.length < 2) return '';
  return TK2_TO_REGION[parseInt(d.slice(0, 2), 10)] || '';
}

// Google hands Greek ΤΚ back in the official spaced form ("121 31"). Store the
// digits alone — that's what WooCommerce's own checkout does (wc_format_postcode)
// and what every downstream consumer (couriers, invoices, analytics) expects.
export function normalizePostcode(postcode) {
  return String(postcode || '').replace(/[\s-]/g, '');
}

// Map a Google place's address_components onto our address fields.
// `states` is the localized GR state map {code: name} for best-effort region.
export function parsePlace(place, states) {
  const comps = (place && place.address_components) || [];
  const get = (type) => {
    const c = comps.find((x) => x.types.indexOf(type) !== -1);
    return c ? c.long_name : '';
  };

  const route = get('route');
  const num = get('street_number');
  const address_1 = [route, num].filter(Boolean).join(' ').trim();
  const postcode = normalizePostcode(get('postal_code'));
  const city = get('locality') || get('postal_town') || get('administrative_area_level_3') || get('administrative_area_level_2');
  const region = get('administrative_area_level_1');

  const out = {};
  if (address_1) out.address_1 = address_1;
  if (postcode) out.postcode = postcode;
  if (city) out.city = city;

  // Prefer Google's region name if present (rare for GR); else derive from TK.
  if (region && states) {
    const norm = (s) => s.toLowerCase().replace(/[ά]/g, 'α').replace(/[έ]/g, 'ε').replace(/[ή]/g, 'η').replace(/[ί]/g, 'ι').replace(/[ό]/g, 'ο').replace(/[ύ]/g, 'υ').replace(/[ώ]/g, 'ω');
    const target = norm(region);
    const hit = Object.keys(states).find((code) => norm(states[code]) === target || norm(states[code]).indexOf(target) !== -1 || target.indexOf(norm(states[code])) !== -1);
    if (hit) out.state = hit;
  }
  if (!out.state) {
    const code = regionFromPostcode(postcode);
    if (code) out.state = code;
  }
  return out;
}
